import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import HandoffVerification from '../components/HandoffVerification';
import VerificationStatus from '../components/VerificationStatus';
import ReviewForm from '../components/ReviewForm';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function RentalStatusPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [forfeitingDeposit, setForfeitingDeposit] = useState(false);
  const [forfeitReason, setForfeitReason] = useState('');
  const [negotiations, setNegotiations] = useState([]);
  const [showProposeForm, setShowProposeForm] = useState(false);
  const [proposeLocation, setProposeLocation] = useState({ name: '', address: '' });
  const [proposing, setProposing] = useState(false);
  const [responding, setResponding] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    Promise.all([
      API.get(`/bookings/${id}`),
      API.get(`/locations/bookings/${id}/negotiations`).catch(() => ({ data: { negotiations: [] } })),
    ]).then(([bookingRes, negoRes]) => {
      setBooking(bookingRes.data.booking);
      setNegotiations(negoRes.data.negotiations || []);
    }).catch(() => navigate('/dashboard/bookings'))
      .finally(() => setLoading(false));
  }, [id, user]);

  const handlePropose = async () => {
    setProposing(true);
    try {
      const { data } = await API.post(`/locations/bookings/${id}/propose-location`, {
        locationName: proposeLocation.name,
        locationAddress: proposeLocation.address || null,
      });
      setNegotiations(p => [...p, data.negotiation]);
      setShowProposeForm(false);
      setProposeLocation({ name: '', address: '' });
      toast.success('Location proposal sent!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send proposal');
    }
    setProposing(false);
  };

  const handleRespond = async (negotiationId, accept) => {
    setResponding(true);
    try {
      await API.patch(`/locations/negotiations/${negotiationId}/respond`, { accept });
      if (accept) {
        const neg = negotiations.find(n => n.id === negotiationId);
        setBooking(p => ({ ...p, meetup_location_name: neg.location_name, meetup_location_address: neg.location_address }));
        toast.success('Location updated!');
      } else {
        toast.success('Location proposal declined');
      }
      setNegotiations(p => p.map(n => n.id === negotiationId ? { ...n, status: accept ? 'accepted' : 'rejected' } : n));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to respond');
    }
    setResponding(false);
  };

  const isRenter = booking && booking.renter_id === user?.id;
  const isOwner = booking && booking.owner_id === user?.id;

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await API.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Confirm pickup with photo
      await API.patch(`/bookings/${id}/confirm-pickup`, {
        photoUrl: data.url,
      });

      setBooking(p => ({
        ...p,
        status: 'active',
        renter_pickup_photo_url: data.url,
        renter_pickup_confirmed_at: new Date().toISOString(),
        owner_funds_released_at: new Date().toISOString(),
      }));

      toast.success('✅ Pickup confirmed! Owner funds released.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmReturn = async () => {
    setConfirming(true);
    try {
      await API.patch(`/bookings/${id}/confirm-return`);
      setBooking(p => ({
        ...p,
        renter_return_confirmed_at: new Date().toISOString(),
      }));
      toast.success('Item return marked. Awaiting owner confirmation.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to confirm return');
    } finally {
      setConfirming(false);
    }
  };

  const handleConfirmReceived = async () => {
    setConfirming(true);
    try {
      await API.patch(`/bookings/${id}/confirm-received`);
      setBooking(p => ({
        ...p,
        status: 'completed',
        deposit_released_at: new Date().toISOString(),
      }));
      toast.success('✅ Item received! Deposit released to renter.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to confirm receipt');
    } finally {
      setConfirming(false);
    }
  };

  const handleForfeitDeposit = async () => {
    if (!forfeitReason.trim()) {
      toast.error('Please provide a reason for forfeiting the deposit');
      return;
    }
    if (!window.confirm('Are you sure? Once forfeited, the deposit cannot be released. This will be marked in your rental history.')) {
      return;
    }
    setForfeitingDeposit(true);
    try {
      await API.patch(`/bookings/${id}/forfeit-deposit`, {
        reason: forfeitReason,
      });
      setBooking(p => ({
        ...p,
        deposit_status: 'forfeited',
        deposit_forfeited_at: new Date().toISOString(),
      }));
      toast.success('✅ Deposit forfeited. Compensation awarded to your account.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to forfeit deposit');
    } finally {
      setForfeitingDeposit(false);
      setForfeitReason('');
    }
  };

  if (loading) return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontSize: 40 }} className="float-anim">🍒</div>
    </div>
  );

  if (!booking) return null;

  const stages = [
    {
      num: 1,
      title: 'Booking Confirmed',
      description: 'Rental is ready for pickup',
      done: ['pending', 'confirmed', 'active', 'completed'].includes(booking.status),
      date: booking.created_at,
    },
    {
      num: 2,
      title: 'Meet & Upload Photo',
      description: isRenter ? 'Meet owner and upload pickup photo' : 'Wait for renter to upload pickup photo',
      done: !!booking.renter_pickup_confirmed_at,
      date: booking.renter_pickup_confirmed_at,
      action: isRenter && booking.status === 'confirmed',
    },
    {
      num: 3,
      title: 'Funds Released',
      description: 'Your payment released to owner',
      done: !!booking.owner_funds_released_at,
      date: booking.owner_funds_released_at,
    },
    {
      num: 4,
      title: 'Item Return Confirmed',
      description: isRenter ? 'Mark item as returned' : 'Wait for item to be returned',
      done: !!booking.renter_return_confirmed_at,
      date: booking.renter_return_confirmed_at,
      action: isRenter && booking.status === 'active' && !booking.renter_return_confirmed_at,
    },
    {
      num: 5,
      title: 'Deposit Released',
      description: 'Full rental complete, deposit refunded',
      done: !!booking.deposit_released_at,
      date: booking.deposit_released_at,
      action: isOwner && booking.renter_return_confirmed_at && !booking.deposit_released_at,
    },
  ];

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '40px 24px 80px' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/dashboard/bookings')}>← Back to Bookings</button>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 8, marginTop: 16 }}>
          📅 Rental: {booking.title}
        </h1>
        <div style={{ fontSize: 14, color: 'var(--text-soft)' }}>
          {format(new Date(booking.start_date), 'MMM d')} – {format(new Date(booking.end_date), 'MMM d, yyyy')} ({booking.days} days)
        </div>
      </div>

      {/* Amount */}
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ color: 'var(--text-soft)' }}>Rental Total</span>
          <span style={{ fontWeight: 700, fontSize: 16 }}>${parseFloat(booking.total_charged).toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-soft)' }}>
          <span>Security Deposit (refundable)</span>
          <span>${parseFloat(booking.deposit_amount).toFixed(2)}</span>
        </div>

        {booking.meetup_location_name && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 8 }}>Meetup Location</div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>📍 {booking.meetup_location_name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>{booking.meetup_location_address}</div>
          </div>
        )}
      </div>

      {/* Location Renegotiation */}
      {(booking.status === 'confirmed' || booking.status === 'active') && (() => {
        const pendingNeg = negotiations.find(n => n.status === 'pending');
        const myRole = isRenter ? 'buyer' : 'seller';
        const otherRole = isRenter ? 'Seller' : 'Renter';
        return (
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>📍 Meetup Location</div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 4 }}>Current location</div>
              <div style={{ fontWeight: 600 }}>{booking.meetup_location_name || 'Not set'}</div>
              {booking.meetup_location_address && <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>{booking.meetup_location_address}</div>}
            </div>

            {pendingNeg && pendingNeg.suggested_by !== myRole && (
              <div style={{ background: '#FFF8E1', border: '1px solid #FFD54F', borderRadius: 10, padding: 14, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>📬 {otherRole} Suggests a New Location</div>
                <div style={{ fontSize: 13, marginBottom: 10 }}>
                  <strong>{pendingNeg.location_name}</strong>
                  {pendingNeg.location_address && <div style={{ color: 'var(--text-soft)', marginTop: 2 }}>{pendingNeg.location_address}</div>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-cherry btn-sm" disabled={responding} onClick={() => handleRespond(pendingNeg.id, true)}>✅ Accept</button>
                  <button className="btn btn-ghost btn-sm" disabled={responding} onClick={() => handleRespond(pendingNeg.id, false)}>❌ Decline</button>
                </div>
              </div>
            )}

            {pendingNeg && pendingNeg.suggested_by === myRole && (
              <div style={{ background: '#F3E5F5', border: '1px solid #CE93D8', borderRadius: 10, padding: 12, marginBottom: 12, fontSize: 13 }}>
                ⏳ Awaiting {otherRole.toLowerCase()} response to your proposal: <strong>{pendingNeg.location_name}</strong>
              </div>
            )}

            {!pendingNeg && negotiations.length < 3 && (
              showProposeForm ? (
                <div>
                  <div className="form-group">
                    <label className="form-label">Location Name <span className="required">*</span></label>
                    <input className="form-input" placeholder="e.g. Starbucks on Main St" value={proposeLocation.name} onChange={e => setProposeLocation(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Address (optional)</label>
                    <input className="form-input" placeholder="123 Main St, Los Angeles, CA" value={proposeLocation.address} onChange={e => setProposeLocation(p => ({ ...p, address: e.target.value }))} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-cherry btn-sm" disabled={!proposeLocation.name || proposing} onClick={handlePropose}>
                      {proposing ? '⏳ Sending…' : '📍 Send Proposal'}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setShowProposeForm(false); setProposeLocation({ name: '', address: '' }); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button className="btn btn-ghost btn-sm" onClick={() => setShowProposeForm(true)}>📍 Suggest Different Location</button>
              )
            )}

            {negotiations.length >= 3 && !pendingNeg && (
              <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Maximum 3 location exchanges reached.</div>
            )}
          </div>
        );
      })()}

      {/* Verification Status */}
      <VerificationStatus bookingId={id} listingType="rent" />

      {/* Handoff Verification */}
      {isRenter && booking.status === 'confirmed' && !booking.renter_pickup_confirmed_at && (
        <HandoffVerification
          bookingId={id}
          userRole="buyer"
          onVerified={() => {
            setBooking(p => ({
              ...p,
              status: 'active',
              renter_pickup_confirmed_at: new Date().toISOString(),
            }));
            toast.success('✅ Handoff verified! Funds released to seller.');
          }}
        />
      )}

      {isOwner && booking.status === 'active' && booking.renter_pickup_confirmed_at && !booking.renter_return_confirmed_at && (
        <HandoffVerification
          bookingId={id}
          userRole="seller"
          onVerified={() => {
            toast.success('✅ Pickup handoff confirmed by both parties.');
          }}
        />
      )}

      {/* Timeline */}
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginTop: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 24 }}>Rental Timeline</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {stages.map((stage, i) => (
            <div key={i}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: stage.action ? 16 : 0 }}>
                {/* Step indicator */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: stage.done ? 'var(--cherry)' : 'var(--cream)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 18,
                    color: stage.done ? 'white' : 'var(--text-soft)',
                    fontWeight: 700,
                  }}>
                    {stage.done ? '✓' : stage.num}
                  </div>
                  {i < stages.length - 1 && (
                    <div style={{
                      width: 2,
                      height: 60,
                      background: stage.done ? 'var(--cherry)' : 'var(--border)',
                      marginTop: 8,
                    }} />
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1, paddingTop: 4 }}>
                  <div style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: stage.done ? 'var(--text-primary)' : 'var(--text-soft)',
                    marginBottom: 4,
                  }}>
                    {stage.title}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 8 }}>
                    {stage.description}
                  </div>
                  {stage.date && (
                    <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                      {format(new Date(stage.date), 'MMM d, h:mm a')}
                    </div>
                  )}
                </div>
              </div>

              {/* Action button */}
              {stage.action && (
                <div style={{ marginLeft: 56, marginBottom: 12 }}>
                  {stage.num === 2 && (
                    <label style={{
                      display: 'inline-block',
                      padding: '10px 18px',
                      background: 'var(--cherry)',
                      color: 'white',
                      borderRadius: 10,
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: 13,
                    }}>
                      {uploading ? 'Uploading...' : '📸 Upload Pickup Photo'}
                      <input type="file" accept="image/*" onChange={handlePhotoUpload} disabled={uploading}
                        style={{ display: 'none' }} />
                    </label>
                  )}
                  {stage.num === 4 && (
                    <button className="btn btn-cherry btn-sm" onClick={handleConfirmReturn} disabled={confirming}>
                      {confirming ? 'Confirming...' : '✅ Confirm Return'}
                    </button>
                  )}
                  {stage.num === 5 && (
                    <button className="btn btn-green btn-sm" onClick={handleConfirmReceived} disabled={confirming}>
                      {confirming ? 'Processing...' : '✅ Confirm Received & Release Deposit'}
                    </button>
                  )}
                </div>
              )}

              {/* Photo preview */}
              {stage.num === 2 && booking.renter_pickup_photo_url && (
                <div style={{ marginLeft: 56 }}>
                  <img src={`${API_BASE}${booking.renter_pickup_photo_url}`}
                    alt="pickup-proof"
                    style={{ maxWidth: 200, borderRadius: 10, border: '2px solid var(--green)' }} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Convert to Purchase option */}
      {booking.status === 'completed' && booking.rent_to_buy_enabled && !booking.converted_to_purchase && booking.rent_to_buy_price && (
        <div style={{ background: '#E3F2FD', borderRadius: 14, padding: 20, marginTop: 24, border: '2px solid #1976D2' }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>🛍️ Want to Keep It?</div>
          <p style={{ fontSize: 14, color: 'var(--text-mid)', marginBottom: 16 }}>
            You can purchase this item now for <strong>${parseFloat(booking.rent_to_buy_price).toFixed(2)}</strong>.
            The item will be yours to keep!
          </p>
          <button
            onClick={() => {
              // Add listing to cart with rent-to-buy flag
              addToCart(booking, { rentalToConvert: true, bookingId: id });
              toast.success('Conversion added to cart! Ready to purchase.');
            }}
            style={{ background: '#1976D2', color: 'white', padding: '12px 24px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
            💳 Purchase Now - ${parseFloat(booking.rent_to_buy_price).toFixed(2)}
          </button>
        </div>
      )}

      {/* Deposit Forfeiture (if item not returned) */}
      {isOwner && booking.renter_return_confirmed_at && !booking.deposit_released_at && booking.deposit_status !== 'forfeited' && (
        <div style={{ background: '#FFE8E8', border: '2px solid #D32F2F', borderRadius: 14, padding: 20, marginTop: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12, color: '#D32F2F' }}>
            ⚠️ Item Not Returned - Forfeit Deposit
          </div>
          <p style={{ fontSize: 14, color: 'var(--text-mid)', marginBottom: 16 }}>
            The renter has claimed the item was returned, but you haven't received it. If the item was not returned or is damaged beyond repair, you can forfeit the deposit as compensation.
          </p>

          <div style={{ background: 'white', borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
              Reason for Forfeiture <span style={{ color: '#D32F2F' }}>*</span>
            </label>
            <textarea
              placeholder="e.g., Item never received, Item returned damaged and beyond repair, etc."
              value={forfeitReason}
              onChange={(e) => setForfeitReason(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e0e0e0',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: "'DM Sans', sans-serif",
                minHeight: 80,
                resize: 'vertical',
              }}
            />
            <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 8 }}>
              This reason will be recorded in the rental history and may be reviewed in disputes.
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={handleForfeitDeposit}
              disabled={forfeitingDeposit || !forfeitReason.trim()}
              style={{
                flex: 1,
                padding: '12px 20px',
                background: '#D32F2F',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 14,
                cursor: forfeitingDeposit || !forfeitReason.trim() ? 'not-allowed' : 'pointer',
                opacity: forfeitingDeposit || !forfeitReason.trim() ? 0.5 : 1,
              }}
            >
              {forfeitingDeposit ? '⏳ Processing...' : '❌ Forfeit Deposit'}
            </button>
            <button
              onClick={() => setForfeitReason('')}
              style={{
                padding: '12px 20px',
                background: 'white',
                color: '#333',
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Deposit Forfeited Confirmation */}
      {booking.deposit_status === 'forfeited' && (
        <div style={{ background: '#FFE8E8', border: '2px solid #D32F2F', borderRadius: 14, padding: 20, marginTop: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#D32F2F' }}>
            ✅ Deposit Forfeited
          </div>
          <p style={{ fontSize: 14, color: 'var(--text-mid)', margin: 0 }}>
            Deposit of <strong>${parseFloat(booking.deposit_amount).toFixed(2)}</strong> has been awarded to your account as compensation.
            Forfeited on {format(new Date(booking.deposit_forfeited_at), 'MMM d, yyyy')}.
          </p>
        </div>
      )}

      {/* Review Form */}
      {booking.status === 'completed' && isRenter && (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 24, marginTop: 24 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, marginBottom: 20 }}>
            📝 Share Your Feedback
          </div>
          <ReviewForm
            referenceId={booking.id}
            referenceType="booking"
            revieweeId={booking.owner_id}
            listingId={booking.listing_id}
            onSuccess={() => {
              toast.success('Review submitted! Thank you for your feedback.');
              setBooking(p => ({ ...p, has_review: true }));
            }}
          />
        </div>
      )}

      {/* Info box */}
      <div style={{ background: '#F5F0EB', borderRadius: 12, padding: 16, marginTop: 24, fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.6 }}>
        <strong>💡 How it works:</strong>
        <ol style={{ marginTop: 8, marginLeft: 16 }}>
          <li>📸 Upload a photo at meetup location to confirm pickup</li>
          <li>💰 Funds are immediately released to the owner</li>
          <li>🔄 When done, mark item as returned</li>
          <li>✅ Owner confirms receipt and your deposit is refunded</li>
          {booking.rent_to_buy_enabled && <li>🛍️ Option to purchase the item after rental completes</li>}
        </ol>
      </div>
    </div>
  );
}
